import { roundToTwoDecimalPlaces } from '@/lib/number';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ShopCartState {
  serial: string;
  title: string;
  price: number;
  count: number;
  weight: number;
}

interface ShopCartSliceState {
  shopId: string;
  title: {
    ko: string;
    en: string;
    ja: string;
    id: string;
    zh: string;
  };
  items: ShopCartState[];
  totalPrice: number;
  redirectUrl: string;
  totalWeight: number;
  productCoupon: ICoupon | null;
  deliveryCoupon: ICoupon | null;
  productCouponDiscountAmount: number;
  deliveryCouponDiscountAmount: number;
  shippingFee: number | null;
  purpleEnergy: number;
  convertedPurpleEnergy: number;
}

const initialShopCartState: ShopCartSliceState = {
  shopId: '',
  title: {
    ko: '',
    en: '',
    ja: '',
    id: '',
    zh: '',
  },
  items: [],
  totalPrice: 0,
  redirectUrl: '',
  totalWeight: 0,
  productCoupon: null,
  deliveryCoupon: null,
  productCouponDiscountAmount: 0,
  deliveryCouponDiscountAmount: 0,
  shippingFee: null,
  purpleEnergy: 0,
  convertedPurpleEnergy: 0,
};

const calculateProductPrice = (state: ShopCartSliceState) => {
  state.totalPrice = state.items.reduce(
    (total, item) => total + roundToTwoDecimalPlaces(item.price * item.count),
    0,
  );
};

const shopCartSlice = createSlice({
  name: 'shopCart',
  initialState: initialShopCartState,
  reducers: {
    setShopId: (state, action: PayloadAction<string>) => {
      state.shopId = action.payload;
    },
    setShopTitle: (
      state,
      action: PayloadAction<{
        ko: string;
        en: string;
        ja: string;
        id: string;
        zh: string;
      }>,
    ) => {
      state.title = action.payload;
    },
    addCart: (state, action: PayloadAction<ShopCartState>) => {
      const newItem = action.payload;

      const existingItem = state.items.find(
        item => item.title === newItem.title,
      );
      if (existingItem) {
        existingItem.count += 1;
      } else {
        state.items.push(newItem);
      }

      calculateProductPrice(state);
      state.totalWeight = state.items.reduce(
        (total, item) => total + item.weight * item.count,
        0,
      );
    },
    removeFromCart: (state, action: PayloadAction<ShopCartState>) => {
      const removeItem = action.payload;

      const existingItem = state.items.find(
        item => item.title === removeItem.title,
      );
      if (existingItem) {
        existingItem.count -= 1;

        if (existingItem.count <= 0) {
          state.items = state.items.filter(
            item => item.title !== removeItem.title,
          );
        }
      }

      state.totalPrice = state.items.reduce(
        (total, item) =>
          total + roundToTwoDecimalPlaces(item.price * item.count),
        0,
      );
      state.totalWeight = state.items.reduce(
        (total, item) => total + item.weight * item.count,
        0,
      );
    },
    deleteCart: (state, action: PayloadAction<ShopCartState>) => {
      const deleteItem = action.payload;

      state.items = state.items.filter(item => item.title !== deleteItem.title);

      state.totalPrice = state.items.reduce(
        (total, item) =>
          total + roundToTwoDecimalPlaces(item.price * item.count),
        0,
      );
      state.totalWeight = state.items.reduce(
        (total, item) => total + item.weight * item.count,
        0,
      );
    },
    allDeleteCart: state => {
      state.items = [];
      state.totalPrice = 0;
      state.totalWeight = 0;
      state.productCoupon = null;
      state.deliveryCoupon = null;
      state.productCouponDiscountAmount = 0;
      state.deliveryCouponDiscountAmount = 0;
      state.shippingFee = null;
    },
    setProductCoupon: (state, action: PayloadAction<ICoupon>) => {
      state.productCoupon = action.payload;
    },
    setDeliveryCoupon: (state, action: PayloadAction<ICoupon>) => {
      state.deliveryCoupon = action.payload;
    },
    setProductCouponDiscountAmount: (state, action: PayloadAction<number>) => {
      state.productCouponDiscountAmount = Math.min(
        action.payload,
        state.totalPrice,
      );
    },
    setDeliveryCouponDiscountAmount: (state, action: PayloadAction<number>) => {
      state.deliveryCouponDiscountAmount = state.shippingFee
        ? Math.min(state.shippingFee, action.payload)
        : action.payload;
    },
    setShippingFee: (state, action: PayloadAction<number>) => {
      state.shippingFee = action.payload;
      state.deliveryCouponDiscountAmount = Math.min(
        state.shippingFee,
        state.deliveryCouponDiscountAmount,
      );
    },
    deleteShippingFee: state => {
      state.shippingFee = null;
    },
  },
});

export const {
  setShopId,
  setShopTitle,
  addCart,
  removeFromCart,
  deleteCart,
  allDeleteCart,
  setProductCoupon,
  setDeliveryCoupon,
  setProductCouponDiscountAmount,
  setDeliveryCouponDiscountAmount,
  setShippingFee,
  deleteShippingFee,
} = shopCartSlice.actions;

export default shopCartSlice.reducer;
